/**
 * Touch Panel document model helpers.
 * Persistence lives in touchPanelService (Firestore, Phase 3).
 */

import { COLOR_PRESETS, DEFAULT_ICON_BG, ICON_TYPE_BUILTIN } from '../../../shared/buttonEditor/constants';
import { deriveInitials } from '../../../shared/buttonEditor/helpers';
import { TOUCH_PANEL_PAYLOAD_LIMITS } from '../../../services/touchPanelDeployable/payloadLimits';

export const PANEL_SCHEMA_VERSION = 1;

export const PANEL_ACTION_OPEN_SCREEN = 'openScreen';
export const PANEL_ACTION_HOME = 'home';
export const PANEL_ACTION_BACK = 'back';
export const PANEL_ACTION_NONE = 'none';
export const PANEL_ACTION_FLOW = 'flow';

export const DEFAULT_PANEL_BG = '#0f172a';

/** Designer preview matches ESP32-P4 panel (720×720), not phone portrait. */
export const PANEL_DEVICE_PREVIEW = TOUCH_PANEL_PAYLOAD_LIMITS.DEFAULT_DISPLAY;

export function clampScreenGridDimension(key, value) {
  const n = Math.floor(Number(value)) || 1;
  if (key === 'rows') {
    return Math.min(
      TOUCH_PANEL_PAYLOAD_LIMITS.MAX_ROWS,
      Math.max(TOUCH_PANEL_PAYLOAD_LIMITS.MIN_ROWS, n)
    );
  }
  if (key === 'columns') {
    return Math.min(
      TOUCH_PANEL_PAYLOAD_LIMITS.MAX_COLUMNS,
      Math.max(TOUCH_PANEL_PAYLOAD_LIMITS.MIN_COLUMNS, n)
    );
  }
  return n;
}

let idCounter = 0;
export function createLocalId(prefix) {
  idCounter += 1;
  return `${prefix}_${Date.now().toString(36)}_${idCounter}`;
}

export function createDefaultScreen({ name = 'Home', sortIndex = 0, rows = 3, columns = 3 } = {}) {
  return {
    id: createLocalId('screen'),
    name,
    sortIndex,
    rows,
    columns,
    appearance: {
      backgroundMode: 'inherit',
      backgroundColor: null,
    },
    buttons: [],
  };
}

/**
 * Seed panel with Home + one extra screen for Open Screen demos.
 */
export function createDefaultPanel() {
  const home = createDefaultScreen({ name: 'Home', sortIndex: 0, rows: 3, columns: 3 });
  const paging = createDefaultScreen({ name: 'Paging', sortIndex: 1, rows: 3, columns: 3 });
  return {
    id: createLocalId('panel'),
    name: 'Office Panel',
    description: '',
    schemaVersion: PANEL_SCHEMA_VERSION,
    defaultScreenId: home.id,
    branding: {
      logoUrl: null,
    },
    appearance: {
      backgroundColor: DEFAULT_PANEL_BG,
    },
    screens: [home, paging],
  };
}

export function createDefaultButton({ position = 0, label = 'New Button' } = {}) {
  return {
    id: createLocalId('btn'),
    label,
    position,
    actionType: PANEL_ACTION_NONE,
    targetScreenId: null,
    flowId: null,
    iconType: ICON_TYPE_BUILTIN,
    iconKey: null,
    iconText: null,
    iconBg: null,
  };
}

export function getScreenById(panel, screenId) {
  return (panel?.screens || []).find((s) => s.id === screenId) || null;
}

export function getSortedScreens(panel) {
  return [...(panel?.screens || [])].sort((a, b) => (a.sortIndex ?? 0) - (b.sortIndex ?? 0));
}

export function buildScreenSlots(screen) {
  const rows = Math.max(1, Number(screen?.rows) || 1);
  const columns = Math.max(1, Number(screen?.columns) || 1);
  const max = rows * columns;
  const slots = Array.from({ length: max }, () => null);
  (screen?.buttons || []).forEach((btn) => {
    const pos = Number(btn.position);
    if (Number.isInteger(pos) && pos >= 0 && pos < max) {
      slots[pos] = btn;
    }
  });
  return slots;
}

/**
 * Move a button to a grid slot. If the target is occupied, swap positions.
 * @returns {object|null} updated screen, or null if no-op / invalid
 */
export function moveButtonOnScreen(screen, fromPosition, toPosition) {
  if (!screen) return null;
  const rows = Math.max(1, Number(screen.rows) || 1);
  const columns = Math.max(1, Number(screen.columns) || 1);
  const max = rows * columns;
  const from = Number(fromPosition);
  const to = Number(toPosition);
  if (!Number.isInteger(from) || !Number.isInteger(to)) return null;
  if (from < 0 || to < 0 || from >= max || to >= max || from === to) return null;

  const slots = buildScreenSlots(screen);
  const moving = slots[from];
  if (!moving) return null;
  const occupant = slots[to];

  const buttons = (screen.buttons || []).map((b) => {
    if (b.id === moving.id) return { ...b, position: to };
    if (occupant && b.id === occupant.id) return { ...b, position: from };
    return b;
  });

  return { ...screen, buttons };
}

export function findFirstEmptySlot(screen) {
  const slots = buildScreenSlots(screen);
  const idx = slots.findIndex((s) => !s);
  return idx >= 0 ? idx : null;
}

/**
 * Resolve effective canvas background for a screen.
 */
export function resolveScreenBackground(panel, screen) {
  if (screen?.appearance?.backgroundMode === 'override' && screen.appearance.backgroundColor) {
    return screen.appearance.backgroundColor;
  }
  return panel?.appearance?.backgroundColor || DEFAULT_PANEL_BG;
}

export function canSavePanelButton(values) {
  if (!(values?.label || '').trim()) return false;
  if (values.actionType === PANEL_ACTION_OPEN_SCREEN) {
    return !!values.targetScreenId;
  }
  // home, back, none, flow (flowId optional for visual-only)
  return true;
}

export function buildPanelButtonSavePayload(values, { fallbackLabel = '' } = {}) {
  const actionType = values.actionType || PANEL_ACTION_NONE;
  const iconType = values.iconType || ICON_TYPE_BUILTIN;
  return {
    label: (values.label || '').trim(),
    actionType,
    targetScreenId:
      actionType === PANEL_ACTION_OPEN_SCREEN ? values.targetScreenId || null : null,
    flowId: actionType === PANEL_ACTION_FLOW ? values.flowId || null : null,
    iconType,
    iconKey: iconType === 'builtin' ? values.iconKey || null : null,
    iconText:
      iconType === 'text'
        ? values.iconText || deriveInitials(values.label || fallbackLabel)
        : null,
    iconBg: iconType === 'text' ? values.iconBg || DEFAULT_ICON_BG : null,
  };
}

/** Presets for panel chrome — one dark default plus distinct colors (no duplicate blacks). */
export const PANEL_BG_PRESETS = [
  DEFAULT_PANEL_BG,
  '#1d4ed8', // blue
  '#0f766e', // teal
  '#b45309', // amber
  '#7c3aed', // violet
  '#be123c', // rose
  ...COLOR_PRESETS,
];

/**
 * Local navigation preview: resolve next screen id from a button press.
 * @returns {{ screenId: string|null, kind: string }}
 */
export function resolveNavigation(panel, currentScreenId, button, historyStack = []) {
  const action = button?.actionType || PANEL_ACTION_NONE;
  if (action === PANEL_ACTION_OPEN_SCREEN) {
    const target = button.targetScreenId;
    if (target && getScreenById(panel, target)) {
      return { screenId: target, kind: 'openScreen', pushHistory: true };
    }
    return { screenId: currentScreenId, kind: 'invalid' };
  }
  if (action === PANEL_ACTION_HOME) {
    return {
      screenId: panel.defaultScreenId || getSortedScreens(panel)[0]?.id || currentScreenId,
      kind: 'home',
      clearHistory: true,
    };
  }
  if (action === PANEL_ACTION_BACK) {
    if (historyStack.length > 0) {
      const prev = historyStack[historyStack.length - 1];
      return { screenId: prev, kind: 'back', popHistory: true };
    }
    return {
      screenId: panel.defaultScreenId || currentScreenId,
      kind: 'back',
      clearHistory: true,
    };
  }
  return { screenId: currentScreenId, kind: action };
}

export function updateScreenInPanel(panel, screenId, updater) {
  return {
    ...panel,
    screens: panel.screens.map((s) => (s.id === screenId ? updater(s) : s)),
  };
}

export function renameScreenInPanel(panel, screenId, name) {
  return updateScreenInPanel(panel, screenId, (s) => ({ ...s, name: name.trim() || s.name }));
}

export function deleteScreenFromPanel(panel, screenId) {
  const remaining = panel.screens.filter((s) => s.id !== screenId);
  if (remaining.length === 0) {
    return panel; // refuse deleting last screen
  }
  const renumbered = remaining
    .sort((a, b) => (a.sortIndex ?? 0) - (b.sortIndex ?? 0))
    .map((s, idx) => ({ ...s, sortIndex: idx }));
  let defaultScreenId = panel.defaultScreenId;
  if (defaultScreenId === screenId) {
    defaultScreenId = renumbered[0].id;
  }
  // Clear openScreen targets pointing at deleted screen
  const scrubbed = renumbered.map((s) => ({
    ...s,
    buttons: (s.buttons || []).map((b) =>
      b.targetScreenId === screenId ? { ...b, targetScreenId: null, actionType: PANEL_ACTION_NONE } : b
    ),
  }));
  return { ...panel, screens: scrubbed, defaultScreenId };
}

export function addScreenToPanel(panel, name = 'New Screen') {
  const sortIndex = panel.screens.length;
  const screen = createDefaultScreen({ name, sortIndex });
  return { ...panel, screens: [...panel.screens, screen] };
}
